import { Button, Icon, IconComponent } from '@graftcode/design-system'

import { MOBILE_MENU_TEST_IDS } from '@common/common-test-ids.const'
import styles from '@common/components/MobileMenu/MobileMenu.module.scss'

import type { CategoryNode, TutorialSection } from '../../types'
import { categoryHasActiveArticle } from '../../utils/categoryTreeUtils'

type CategorySectionProps = {
  category: CategoryNode
  basePath: string
  currentTutorialPath?: string
  expandedCategories: Set<string>
  onToggleCategory: (categoryPath: string) => void
  onSectionClick: (section: TutorialSection, basePath: string) => void
}

export function CategorySection({
  category,
  basePath,
  currentTutorialPath,
  expandedCategories,
  onToggleCategory,
  onSectionClick,
}: CategorySectionProps) {
  const categoryKey = `${basePath}-${category.path}`
  const isExpanded = expandedCategories.has(categoryKey)
  const hasContent = category.articles.length > 0 || category.children.length > 0
  const hasActive = categoryHasActiveArticle(category, currentTutorialPath)

  if (!hasContent) return null

  return (
    <div className={styles.academyCategory} data-testid={MOBILE_MENU_TEST_IDS.academyCategorySection}>
      <button
        type="button"
        className={`${styles.academyCategoryHeader} ${hasActive ? styles.hasActive : ''}`}
        onClick={() => onToggleCategory(categoryKey)}
        aria-expanded={isExpanded}
      >
        <IconComponent
          icon={Icon.ChevronRightRegular}
          size={16}
          className={`${styles.academyCategoryChevron} ${isExpanded ? styles.expanded : ''}`}
        />
        <span className={styles.academyCategoryTitle}>{category.title}</span>
      </button>

      {isExpanded && (
        <div className={styles.academyCategoryContent}>
          {category.children
            .sort((a, b) => a.order - b.order)
            .map((child) => (
              <CategorySection
                key={child.path}
                category={child}
                basePath={basePath}
                currentTutorialPath={currentTutorialPath}
                expandedCategories={expandedCategories}
                onToggleCategory={onToggleCategory}
                onSectionClick={onSectionClick}
              />
            ))}

          {category.articles
            .sort((a, b) => a.order - b.order)
            .map((section) => (
              <Button
                key={section.path}
                variant="ghost"
                size="l"
                fullWidth
                text={section.title}
                onClick={() => onSectionClick(section, basePath)}
                className={`${styles.academyArticle} ${currentTutorialPath === section.path ? styles.active : ''}`}
                style={{ justifyContent: 'flex-start' }}
              />
            ))}
        </div>
      )}
    </div>
  )
}
